import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "./motion";
import { CATEGORIES, OTHER_BOOKS, amazonUrl } from "../data/library";

const EASE = [0.16, 1, 0.3, 1];

function BookCard({ book }) {
  return (
    <motion.article
      layout
      data-testid={`library-book-${book.id}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.5, ease: EASE }}
      className={`group flex gap-5 border border-line bg-ink2 p-5 transition-colors duration-500 hover:border-vermilion/40 ${
        book.featured ? "md:col-span-2 md:flex-row" : ""
      }`}
    >
      <a
        href={amazonUrl(book)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`buy-${book.id}`}
        aria-label={`Buy ${book.title} on Amazon`}
        className="relative w-24 shrink-0 overflow-hidden border border-line sm:w-28"
      >
        <img
          src={`/assets/covers/${book.id}.jpg`}
          alt={`${book.title} — cover`}
          loading="lazy"
          className="aspect-[3/4] h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </a>
      <div className="flex min-w-0 flex-1 flex-col justify-between gap-4">
        <div>
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-vermilion">
            {book.category}
          </p>
          <h3 className="font-serif text-2xl leading-tight text-bone">{book.title}</h3>
          {book.subtitle && (
            <p className="mt-1 text-sm italic leading-snug text-ash">{book.subtitle}</p>
          )}
          {book.coauthor && (
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
              {book.coauthor}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1.5">
            {book.formats.map((f) => (
              <span
                key={f}
                className="border border-line px-2 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-smoke"
              >
                {f}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {book.volumes ? (
              book.volumes.map((v) => (
                <a
                  key={v.asin}
                  href={amazonUrl(v)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`buy-link-${book.id}-${v.label.replace(/\W/g, "").toLowerCase()}`}
                  className="inline-flex items-center gap-1 border border-vermilion/40 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-vermilion transition-colors duration-300 hover:bg-vermilion hover:text-ink"
                >
                  {v.label}
                  <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
                </a>
              ))
            ) : (
              <a
                href={amazonUrl(book)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`buy-link-${book.id}`}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-vermilion transition-colors duration-300 hover:text-bone"
              >
                Buy on Amazon
                <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
              </a>
            )}
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Library() {
  const [active, setActive] = useState("All");
  const books = OTHER_BOOKS.filter((b) => active === "All" || b.category === active);

  return (
    <div data-testid="library-section" className="mt-24">
      <FadeUp className="mb-10 flex flex-col gap-6 border-t border-line pt-10 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-vermilion">
            Beyond the Lexicon
          </p>
          <h3 className="font-serif text-3xl font-light leading-tight tracking-tight sm:text-5xl">
            Psychology, technology
            <br />
            &amp; narrative<span className="text-vermilion">.</span>
          </h3>
        </div>
        <div data-testid="library-filters" className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`library-filter-${c.toLowerCase()}`}
              onClick={() => setActive(c)}
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                active === c
                  ? "border-vermilion bg-vermilion text-ink"
                  : "border-line text-ash hover:border-bone hover:text-bone"
              }`}
            >
              {c}
              <span className="ml-2 text-[9px] opacity-60">
                {c === "All" ? OTHER_BOOKS.length : OTHER_BOOKS.filter((b) => b.category === c).length}
              </span>
            </button>
          ))}
        </div>
      </FadeUp>

      <motion.div layout className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <AnimatePresence mode="popLayout">
          {books.map((b) => (
            <BookCard key={b.id} book={b} />
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
